import {connect} from 'react-redux';

import {createSelector, makeMapStateToProps} from '../../../../../srcCommon/helpers/reselectHelpers';
import {
  buildTeammateInboxLabelSectionExpandedKey,
  CollapsedItemStates,
  computeIsItemExpanded,
} from '../../../../helpers/expansionTogglingHelpers';
import {VisualCustomView} from '../../../../helpers/models/customViewHelpers';
import {TeamModel} from '../../../../models/api/teamModel';
import {AppState} from '../../../../store/appState';
import {getUserOwnedInboxes} from '../../../../store/data/inboxes/inboxesSelectors';
import {getUserStarredTagId} from '../../../../store/data/tags/tagsSelectors';
import {getTeams} from '../../../../store/data/teams/teamsSelectors';
import {WorkspaceDropReorderTeammateWorkspaceItemHandler} from '../../../../store/data/workspace/reorder/types';
import {buildTeammateWorkspaceExpandedKey} from '../../../../store/data/workspace/workspaceExpandedSelectors';
import {getHasPreventPinTeamTags} from '../../../../store/featureFlags/featureFlagsSelectors';
import {canDropWorkspaceItemAtTarget} from '../dnd/workspaceDragDropHelpers';
import {WorkspaceDragSourceTypesEnum, WorkspaceItemDropHandler} from '../dnd/workspaceDragDropTypes';
import {ToggleExpandItemHandler} from './context/types';
import {TeammateWorkspaceEditor} from './teammateWorkspaceEditor';

const defaultIsWorkspaceExpanded = true;
const defaultIsInboxExpanded = true;

/*
 * Props.
 */

interface TeammateWorkspaceEditorContainerProps {
  /** Hash map of workspace item expansion toggle states. */
  collapsedItems: CollapsedItemStates;
  /** Handler for toggling the expanded/collapsed state of section items. */
  onToggleExpandSection: ToggleExpandItemHandler;
  /** The IDs of teams the user is a member of. */
  teamIds: number[];
  onDropReorderNestedFolder: WorkspaceDropReorderTeammateWorkspaceItemHandler;
  userNamespace: string;
}

/*
 * Selectors.
 */

function getTeamIds(_state: AppState, props: TeammateWorkspaceEditorContainerProps) {
  return props.teamIds;
}

function makeGetUserTeams() {
  return createSelector([getTeams, getTeamIds], (teams, ids) => ids.map((id) => teams[id]));
}

function makeCanAddTeamTags() {
  return createSelector([getHasPreventPinTeamTags], (hasPreventPinTeamTags) => !hasPreventPinTeamTags);
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<TeammateWorkspaceEditorContainerProps>()(() => ({
  userStarredTagId: getUserStarredTagId,
  inboxes: getUserOwnedInboxes,
  teams: makeGetUserTeams(),
  canAddTeamTags: makeCanAddTeamTags(),
}));

export type ToggleVisualCustomViewHandler = (vcv: VisualCustomView) => void;

/**
 * This component is adapted from <TeammateWorkspaceContainer /> but for use in <WorkspacesEditor />.
 * @see TeammateWorkspaceContainer
 */
export const TeammateWorkspaceEditorContainer = connect(
  mapStateToProps,
  null,
  (stateProps, dispatchProps, ownProps) => {
    const {collapsedItems, onToggleExpandSection, userNamespace} = ownProps;
    const {teams} = stateProps;

    const workspaceKey = buildTeammateWorkspaceExpandedKey(userNamespace);
    const isExpanded = computeIsItemExpanded(collapsedItems, workspaceKey, defaultIsWorkspaceExpanded);
    const onToggleExpand = () =>
      onToggleExpandSection({key: workspaceKey, defaultExpandedState: defaultIsWorkspaceExpanded});

    const workspaceInboxKey = buildTeammateInboxLabelSectionExpandedKey(userNamespace);
    const isInboxExpanded = computeIsItemExpanded(collapsedItems, workspaceInboxKey, defaultIsInboxExpanded);
    const onToggleInboxExpand = () =>
      onToggleExpandSection({key: workspaceInboxKey, defaultExpandedState: defaultIsInboxExpanded});

    const onDropReorderNestedFolder: WorkspaceItemDropHandler = (droppedItem, linePosition, target) => {
      if (!target || !canDropWorkspaceItemAtTarget(droppedItem, target)) {
        return;
      }

      if (
        droppedItem.type !== WorkspaceDragSourceTypesEnum.PERSONAL_FOLDER ||
        target.type !== WorkspaceDragSourceTypesEnum.PERSONAL_FOLDER
      ) {
        return;
      }

      ownProps.onDropReorderNestedFolder({
        droppedFolder: droppedItem,
        targetFolder: target,
        linePosition,
      });
    };

    // Merge the props.
    return {
      ...stateProps,
      ...ownProps,
      isExpanded,
      isInboxExpanded,
      onToggleExpand,
      onToggleInboxExpand,
      onDropReorderNestedFolder,
      teams: teams.reduce<TeamModel[]>((result, team) => {
        if (team !== undefined) {
          result.push(team);
        }
        return result;
      }, []),
    };
  },
)(TeammateWorkspaceEditor);
